import { CalendarDate, getDayOfWeek, parseDate } from '@internationalized/date'

import type { EnvelopeId } from './envelope'
import type { OccurrenceId } from './ledger'
import type { Xof } from './money'
import type { PeriodBounds } from './period'

/**
 * Revenus et dépenses récurrents (spec §3.5, §4 étapes 2 et 4).
 * Une règle est un modèle ; ses occurrences sont générées pour chaque période avec un
 * identifiant déterministe `règle:AAAA-MM-JJ`, ce qui interdit les doublons.
 * Une occurrence prévue n'est jamais une écriture : elle le devient sur confirmation.
 */

export type RecurringFrequency =
  | { kind: 'weekly'; weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7 }
  | { kind: 'monthly'; day: number }
  | { kind: 'yearly'; month: number; day: number }

export interface RecurringRule {
  id: string
  direction: 'income' | 'expense'
  title: string
  /** Montant prévu. En mode variable, il sert de valeur proposée, modifiable au paiement. */
  amount: Xof
  amountMode: 'fixed' | 'variable'
  /** Enveloppe débitée, pour une dépense. `null` pour un revenu. */
  envelopeId: EnvelopeId | null
  frequency: RecurringFrequency
  /** Date de début incluse (AAAA-MM-JJ). */
  startsOn: string
  /** Date de fin incluse, `null` si sans fin. */
  endsOn: string | null
  /** Rappels, en jours avant l'échéance. */
  reminderOffsets: readonly number[]
  /** Réserve automatiquement le montant dans l'enveloppe à l'ouverture de la période. */
  reserveInAdvance: boolean
  isActive: boolean
}

export interface Occurrence {
  id: OccurrenceId
  ruleId: string
  dueOn: CalendarDate
  expectedAmount: Xof
}

export type OccurrenceStatus = 'upcoming' | 'due' | 'overdue' | 'settled' | 'skipped'

export function occurrenceId(ruleId: string, dueOn: CalendarDate): OccurrenceId {
  return `${ruleId}:${dueOn.toString()}`
}

/** Jour du mois borné au dernier jour réel (31 → 28, 29, 30 selon le mois). */
function clampedDay(year: number, month: number, day: number): CalendarDate {
  const first = new CalendarDate(year, month, 1)
  const lastDay = first.calendar.getDaysInMonth(first)
  return new CalendarDate(year, month, Math.min(day, lastDay))
}

function datesBetween(rule: RecurringRule, from: CalendarDate, to: CalendarDate): CalendarDate[] {
  const dates: CalendarDate[] = []
  const frequency = rule.frequency
  switch (frequency.kind) {
    case 'weekly': {
      // getDayOfWeek avec la locale fr-FR : 0 = lundi … 6 = dimanche.
      const offset = (frequency.weekday - 1 - getDayOfWeek(from, 'fr-FR') + 7) % 7
      for (
        let date = from.add({ days: offset });
        date.compare(to) <= 0;
        date = date.add({ weeks: 1 })
      ) {
        dates.push(date)
      }
      break
    }
    case 'monthly':
    case 'yearly': {
      for (
        let cursor = new CalendarDate(from.year, from.month, 1);
        cursor.compare(to) <= 0;
        cursor = cursor.add({ months: 1 })
      ) {
        if (frequency.kind === 'yearly' && cursor.month !== frequency.month) continue
        const date = clampedDay(cursor.year, cursor.month, frequency.day)
        if (date.compare(from) >= 0 && date.compare(to) <= 0) dates.push(date)
      }
      break
    }
  }
  return dates
}

/** Occurrences d'une règle dont l'échéance tombe dans la période. */
export function occurrencesInPeriod(rule: RecurringRule, bounds: PeriodBounds): Occurrence[] {
  if (!rule.isActive) return []
  const startsOn = parseDate(rule.startsOn)
  const endsOn = rule.endsOn === null ? null : parseDate(rule.endsOn)
  const from = startsOn.compare(bounds.firstDay) > 0 ? startsOn : bounds.firstDay
  const to = endsOn !== null && endsOn.compare(bounds.lastDay) < 0 ? endsOn : bounds.lastDay
  if (from.compare(to) > 0) return []
  return datesBetween(rule, from, to).map((dueOn) => ({
    id: occurrenceId(rule.id, dueOn),
    ruleId: rule.id,
    dueOn,
    expectedAmount: rule.amount,
  }))
}

/**
 * Statut d'une occurrence. `settled` et `skipped` viennent du journal et des décisions de
 * l'utilisateur ; sinon le statut dépend de la date du jour. Une échéance n'est jamais
 * considérée comme payée parce que sa date est passée (spec §5.4).
 */
export function occurrenceStatus(
  occurrence: Occurrence,
  today: CalendarDate,
  facts: { settled: boolean; skipped: boolean },
): OccurrenceStatus {
  if (facts.settled) return 'settled'
  if (facts.skipped) return 'skipped'
  const comparison = occurrence.dueOn.compare(today)
  if (comparison > 0) return 'upcoming'
  if (comparison === 0) return 'due'
  return 'overdue'
}

/** Jours avant l'échéance (négatif si elle est passée). */
export function daysUntil(occurrence: Occurrence, today: CalendarDate): number {
  return occurrence.dueOn.compare(today)
}

/** Rappels à envoyer aujourd'hui pour une occurrence non réglée. */
export function remindersDueToday(
  rule: RecurringRule,
  occurrence: Occurrence,
  today: CalendarDate,
): number[] {
  const remaining = daysUntil(occurrence, today)
  return rule.reminderOffsets.filter((offset) => offset === remaining)
}
